import { useEffect, useRef, useState } from 'react';
import { getPresence, openPlanEvents, readEvents } from './apiLive';
import type { PresenceUser } from './types';

export type LiveStatus = 'connecting' | 'live';

export interface LiveHandlers {
  /**
   * Fetch the plan again. Bursts arrive coalesced. `version` is the newest version the
   * server announced; null means "something may have been missed", so always reload.
   */
  onChanged: (version: number | null) => void;
  /** This person was taken off the plan. The stream is closed and stays closed. */
  onRemoved?: () => void;
  /** The server refused the stream: this person has it open in too many places. */
  onTooManyStreams?: () => void;
}

export interface UseNodePlanLive {
  /** Who has the plan open right now, this person included. */
  presence: PresenceUser[];
  /** The newest plan version the stream has announced. */
  version: number | null;
  status: LiveStatus;
}

const COALESCE_MS = 300;
const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];
/** Wait this long for a leader to answer before taking the stream. */
const ELECT_MS = 300;
const BEAT_MS = 10000;
/** The server writes a heartbeat every 20 s and the leader relays each one, so two missed means gone. */
const LEADER_TIMEOUT_MS = 45000;
const WATCH_MS = 5000;
/** A stream that stayed open this long was healthy; the next reconnect starts from the first step. */
const HEALTHY_MS = 30000;

type Wire =
  | { type: 'who'; from: string }
  | { type: 'leader'; from: string; presence: PresenceUser[] | null; version: number | null; live: boolean }
  | { type: 'resign'; from: string }
  | { type: 'event'; from: string; name: string; data: unknown };

type WireOut =
  | { type: 'who' }
  | { type: 'leader'; presence: PresenceUser[] | null; version: number | null; live: boolean }
  | { type: 'resign' }
  | { type: 'event'; name: string; data: unknown };

const tabId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const samePeople = (a: PresenceUser[], b: PresenceUser[]) =>
  a.length === b.length && a.every((p, i) => p.user_id === b[i].user_id && p.firstname === b[i].firstname && p.lastname === b[i].lastname);

const asVersion = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Live updates for one plan. One stream per browser, not per tab: the tabs showing
 * this plan elect a leader over a BroadcastChannel, the leader holds the stream and
 * relays every event, and the rest listen. Without BroadcastChannel every tab streams.
 */
export function useNodePlanLive(planId: string, handlers: LiveHandlers): UseNodePlanLive {
  const [presence, setPresence] = useState<PresenceUser[]>([]);
  const [version, setVersion] = useState<number | null>(null);
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const me = tabId();
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(`node-plan-live:${planId}`) : null;
    let disposed = false;
    let stopped = false;
    let leading = false;
    let leaderId: string | null = null;
    let lastLeaderSign = 0;
    let abort: AbortController | null = null;
    let attempt = 0;
    let openedAt = 0;
    let retryTimer: number | null = null;
    let beatTimer: number | null = null;
    let electTimer: number | null = null;
    let coalesceTimer: number | null = null;
    // undefined: nothing waiting; null: reload whatever the version.
    let pending: number | null | undefined;
    let lastPresence: PresenceUser[] | null = null;
    let lastVersion: number | null = null;
    /** Leader only: the stream is open and has said hello. */
    let streamUp = false;
    let toldTooMany = false;

    setPresence([]);
    setVersion(null);
    setStatus('connecting');

    const post = (msg: WireOut) => {
      if (!channel) return;
      try {
        channel.postMessage({ ...msg, from: me });
      } catch {
        /* the channel closed under us: the tab is going away */
      }
    };

    const clear = (t: number | null) => {
      if (t !== null) window.clearTimeout(t);
    };

    const fire = () => {
      coalesceTimer = null;
      const v = pending;
      pending = undefined;
      if (disposed || stopped || v === undefined) return;
      handlersRef.current.onChanged(v);
    };

    const requestReload = (v: number | null, delay: number) => {
      pending = pending === undefined ? v : pending === null || v === null ? null : Math.max(pending, v);
      if (coalesceTimer !== null) {
        if (delay > 0) return;
        clear(coalesceTimer);
      }
      coalesceTimer = window.setTimeout(fire, delay);
    };

    const stepDown = () => {
      if (!leading) return;
      leading = false;
      streamUp = false;
      if (beatTimer !== null) window.clearInterval(beatTimer);
      beatTimer = null;
      clear(retryTimer);
      retryTimer = null;
      abort?.abort();
      abort = null;
    };

    const showPresence = (next: PresenceUser[]) => {
      // The leader repeats presence on every heartbeat; only a real change re-renders.
      if (lastPresence && samePeople(lastPresence, next)) return;
      lastPresence = next;
      setPresence(next);
    };

    const stopAll = () => {
      stopped = true;
      stepDown();
      clear(electTimer);
      clear(coalesceTimer);
      electTimer = null;
      coalesceTimer = null;
      pending = undefined;
    };

    // Every tab, the leader included, takes events through here.
    const handle = (name: string, data: unknown) => {
      if (disposed || stopped) return;
      const d = (data ?? {}) as { version?: unknown; presence?: unknown };
      switch (name) {
        case 'hello': {
          const v = asVersion(d.version);
          lastVersion = v;
          if (Array.isArray(d.presence)) showPresence(d.presence as PresenceUser[]);
          setVersion(v);
          setStatus('live');
          if (leading) streamUp = true;
          // A first connect or a reconnect: anything written meanwhile is behind this version.
          requestReload(v, 0);
          break;
        }
        case 'changed': {
          const v = asVersion(d.version);
          if (v !== null) {
            lastVersion = Math.max(lastVersion ?? v, v);
            setVersion(lastVersion);
          }
          requestReload(v, COALESCE_MS);
          break;
        }
        case 'presence':
          if (Array.isArray(d.presence)) showPresence(d.presence as PresenceUser[]);
          break;
        case 'removed':
          stopAll();
          handlersRef.current.onRemoved?.();
          break;
        case 'down':
          streamUp = false;
          setStatus('connecting');
          break;
        default:
          break;
      }
    };

    /** The leader's copy of an event: handled here, relayed to every other tab. */
    const relay = (name: string, data: unknown) => {
      handle(name, data);
      post({ type: 'event', name, data });
    };

    const beat = () => post({ type: 'leader', presence: lastPresence, version: lastVersion, live: streamUp });

    const scheduleReconnect = () => {
      if (disposed || stopped || !leading) return;
      clear(retryTimer);
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      attempt += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        connect();
      }, delay);
    };

    const connect = async () => {
      if (disposed || stopped || !leading) return;
      const ctrl = new AbortController();
      abort = ctrl;
      const live = () => !disposed && !stopped && leading && abort === ctrl;
      try {
        let res = await openPlanEvents(planId, ctrl.signal);
        if (res?.status === 401 && live()) {
          // An expired token. Any call through apiFetchAuthed refreshes it, or signs the
          // person out when it cannot; then the stream gets one more try.
          await Promise.resolve(getPresence(planId)).catch(() => undefined);
          if (!live()) return;
          res = await openPlanEvents(planId, ctrl.signal);
        }
        if (!live()) return;
        if (!res || res.status === 401) {
          // Still refused after a refresh: the session is over and the page shows it.
          return;
        }
        if (res.status === 403) {
          const body = await res.json().catch(() => null);
          // Not a member (any more): the same page a removal lands on. Any other refusal just stops.
          if (live() && body?.error?.code === 'not_a_member') relay('removed', {});
          return;
        }
        if (res.status === 404) return; // The plan is gone; the page's own load says so.
        if (res.status === 429) {
          if (!toldTooMany) {
            toldTooMany = true;
            handlersRef.current.onTooManyStreams?.();
          }
          scheduleReconnect();
          return;
        }
        if (!res.ok) throw new Error(`events ${res.status}`);
        openedAt = Date.now();
        await readEvents(
          res,
          (name, data) => {
            if (live()) relay(name, data);
          },
          () => {
            // A hidden tab's timers slow to a crawl; stream reads do not, so the
            // server's heartbeat is what keeps the other tabs sure of this one.
            if (live()) beat();
          },
        );
      } catch {
        if (ctrl.signal.aborted) return;
      }
      if (!live()) return;
      if (openedAt && Date.now() - openedAt >= HEALTHY_MS) attempt = 0;
      openedAt = 0;
      relay('down', {});
      scheduleReconnect();
    };

    const becomeLeader = () => {
      if (disposed || stopped || leading) return;
      clear(electTimer);
      electTimer = null;
      leading = true;
      leaderId = me;
      attempt = 0;
      beat();
      if (channel) beatTimer = window.setInterval(beat, BEAT_MS);
      connect();
    };

    const leaderSilent = () => Date.now() - lastLeaderSign > LEADER_TIMEOUT_MS;

    const claimSoon = () => {
      // Every follower waits a random moment, so usually one claims and the rest hear it.
      clear(electTimer);
      electTimer = window.setTimeout(() => {
        electTimer = null;
        if (!leaderId || leaderSilent()) becomeLeader();
      }, Math.round(Math.random() * 200));
    };

    const onMessage = (e: MessageEvent) => {
      if (disposed || stopped) return;
      const msg = (e.data || {}) as Wire;
      if (!msg.from || msg.from === me) return;
      switch (msg.type) {
        case 'who':
          if (leading) beat();
          break;
        case 'leader':
          if (leading) {
            // Two leaders: the lower id keeps the stream, so both sides agree without talking.
            if (msg.from < me) {
              stepDown();
              leaderId = msg.from;
              lastLeaderSign = Date.now();
            } else beat();
            break;
          }
          leaderId = msg.from;
          lastLeaderSign = Date.now();
          clear(electTimer);
          electTimer = null;
          if (Array.isArray(msg.presence)) showPresence(msg.presence);
          // A tab that joins after the hello learns the stream's state from the beat.
          if (msg.version !== null && msg.version !== undefined && msg.version !== lastVersion) {
            lastVersion = msg.version;
            setVersion(msg.version);
          }
          setStatus(msg.live ? 'live' : 'connecting');
          break;
        case 'resign':
          if (msg.from === leaderId) {
            leaderId = null;
            claimSoon();
          }
          break;
        case 'event':
          if (leading) break;
          leaderId = msg.from;
          lastLeaderSign = Date.now();
          handle(msg.name, msg.data);
          break;
        default:
          break;
      }
    };

    const watch = channel
      ? window.setInterval(() => {
          if (!disposed && !stopped && !leading && leaderSilent()) becomeLeader();
        }, WATCH_MS)
      : null;

    const onVisible = () => {
      if (document.hidden || disposed || stopped) return;
      // Back on the tab: whatever the stream said while away, fetch the plan fresh.
      requestReload(null, 0);
      if (!leading && channel && leaderSilent()) becomeLeader();
    };

    const onOnline = () => {
      if (leading && retryTimer !== null) {
        clear(retryTimer);
        retryTimer = null;
        connect();
      }
    };

    const resign = () => {
      if (leading) post({ type: 'resign' });
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener('pagehide', resign);

    if (channel) {
      channel.addEventListener('message', onMessage);
      post({ type: 'who' });
      electTimer = window.setTimeout(() => {
        electTimer = null;
        if (!leaderId) becomeLeader();
      }, ELECT_MS);
    } else {
      becomeLeader();
    }

    return () => {
      resign();
      stepDown();
      disposed = true;
      clear(electTimer);
      clear(coalesceTimer);
      if (watch !== null) window.clearInterval(watch);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pagehide', resign);
      if (channel) {
        channel.removeEventListener('message', onMessage);
        channel.close();
      }
    };
  }, [planId]);

  return { presence, version, status };
}
